import {
  DEFAULT_MATERIAL_TAX_RATE,
  DEFAULT_WORK_TAX_RATE,
  PriceBook,
  ROOF_PATHS,
  defaultColorFor,
  defaultDeckAnswers,
  defaultExposedFastenerAnswers,
  defaultFlatRoofAnswers,
  defaultSidingAnswers,
  generatePlan,
  fitFramingNails,
  generationRecord,
  isDeckType,
  isSidingType,
  linesToPlan,
  roofsForOrder,
  sidingLineColor,
  sidingPanelProductIds,
  sidingSquaresOf,
  templateIsUnbuilt,
  templateQuestions,
  toDecimal,
  templatesForPath,
  UNBUILT_TEMPLATE_EXPLANATION,
  UNBUILT_TEMPLATE_LABEL,
  type ColorProduct,
  type ExposedFastenerAnswers,
  type FlatRoofAnswers,
  type GeneratorProduct,
  type Job,
  type PlanProduct,
  type PricedSupplier,
  type ProductPrice,
  type RoofLayout,
  type RoofPath,
  type RoofTemplate,
  type RoofTemplateItem,
  type DeckAnswers,
  type SidingAnswers,
  type Supplier,
  type WallMeasurement,
} from "@roof-hawk/core";
import { useState } from "react";

import { Modal } from "../../components/ui";
import { t } from "../../i18n";
import { fetchFramingNails } from "../../lib/framingNails";
import { useAsync } from "../../lib/hooks";
import { insertRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";
import { FlatRoofQuestions } from "../estimate/FlatRoofQuestions";
import { MetalPanelQuestions } from "../estimate/MetalPanelQuestions";
import { DeckQuestions } from "../estimate/DeckQuestions";
import { SidingQuestions } from "../estimate/SidingQuestions";
import { PATH_CLASS } from "../estimate/paths";

/**
 * A material order or a work order worked out from the roof, on an estimate
 * that already exists.
 *
 * Jonathon, 2026-08-24: "when creating a material order or work order we should
 * be able to have an option from measurements. that way if we want to use a
 * template we can without having to also use it's verbiage or anything else."
 *
 * Until now a template could only be used at the moment an estimate was
 * created, and using one brought the whole package with it — the scope of work,
 * the What You Get lines, the warranty wording, a headline price solved to the
 * target margin, and both orders. A rep who had written the customer's side by
 * hand and only wanted the quantities had no way to ask for them, so they typed
 * a material order out line by line off a template they could see in Tools.
 *
 * So this is the same arithmetic — `generatePlan` in packages/core, the one the
 * estimate generator uses, so a material order built here and one built there
 * are the same order — with everything that is not a quantity left out. It
 * writes lines onto orders and touches nothing else: no estimate items, no
 * scope, no price, no warranty. The estimate's own customer-facing side stays
 * exactly as the rep wrote it.
 *
 * Other expenses are deliberately not offered here. They are not measured off a
 * roof — the permit, the general liability, the warranty are worked out from
 * the estimate's own revenue by `computeAutoExpenses`, and the rest are typed —
 * so "from measurements" would have nothing to mean. Jonathon: "the other
 * expenses can still operate as normal."
 */
export function GenerateOrderModal({
  job,
  estimateId,
  /** Which order to write. The plan produces both; only one is kept. */
  kind,
  trade,
  suppliers,
  onClose,
  onCreated,
}: {
  job: Job;
  estimateId: string;
  kind: "material" | "work";
  /** The estimate's trade, so a generated order lands on the same one. */
  trade: string | null;
  suppliers: readonly Supplier[];
  onClose: () => void;
  /** The first order written, so the caller can open it. */
  onCreated: (orderId: string) => Promise<void>;
}) {
  const { profileId } = useSession();
  const [roofPath, setRoofPath] = useState<RoofPath>("Shingle");
  const [templateId, setTemplateId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * The same questions the estimate builder asks, for the same reason: on a
   * flat or metal panel template the answers decide which lines exist and, on
   * metal, which part each line is. Without them this screen ordered every
   * line the template carries — both panel screws, the furring strips and the
   * touch-up paint on a galvalume roof on the deck. See `linesToPlan`.
   */
  const [flatAnswers, setFlatAnswers] = useState<FlatRoofAnswers>(
    defaultFlatRoofAnswers,
  );
  const [metalAnswers, setMetalAnswers] = useState<ExposedFastenerAnswers>(
    defaultExposedFastenerAnswers,
  );
  /** And the siding questions, null until touched (see the estimate builder). */
  const [sidingOverride, setSidingAnswers] = useState<SidingAnswers | null>(
    null,
  );
  /** And the deck's size, null until touched: the estimate's own stand in. */
  const [deckOverride, setDeckAnswers] = useState<DeckAnswers | null>(null);

  const templates = useAsync(
    async () =>
      unwrap<RoofTemplate[]>(
        await supabase
          .from("roof_templates")
          .select("*")
          .eq("active", true)
          .order("sort_order")
          .order("name"),
      ),
    [],
  );

  // Every roof on the job, not the first one. A house with a detached garage is
  // two layouts, and ordering for one of them ships a short load.
  const layouts = useAsync(
    async () =>
      unwrap<RoofLayout[]>(
        await supabase
          .from("job_measurements")
          .select("*")
          .eq("job_id", job.id)
          .order("captured_at"),
      ),
    [job.id],
  );

  // The walls, for a siding template. Apart from the roof (siding.ts).
  const walls = useAsync(
    async () =>
      unwrap<WallMeasurement[]>(
        await supabase
          .from("job_wall_measurements")
          .select("*")
          .eq("job_id", job.id)
          .order("captured_at"),
      ),
    [job.id],
  );

  /*
   * The estimate's frozen copy, for its areas in another material (ADR 0184).
   * A metal porch on a shingle house was taken off the house when the
   * estimate was generated; regenerating the shingle order against the whole
   * roof bought shingles for the porch as well.
   */
  const estimateRow = useAsync(
    async () =>
      unwrap<{ generation_snapshot: unknown; generation_answers: unknown }[]>(
        await supabase
          .from("estimates")
          .select("generation_snapshot,generation_answers")
          .eq("id", estimateId),
      )[0] ?? null,
    [estimateId],
  );

  const offered = templatesForPath(templates.data ?? [], roofPath);
  const picked = (templates.data ?? []).find((tpl) => tpl.id === templateId);
  // A siding template is measured on the walls; everything else on the roof.
  const onWalls = isSidingType(picked?.roof_type);
  const wallRows = walls.data ?? [];
  const measured = onWalls ? wallRows : (layouts.data ?? []);
  const sized = roofsForOrder(
    estimateRow.data?.generation_snapshot,
    templateId,
    measured,
    onWalls ? "walls" : "roof",
  );
  const roofs = sized.roofs;
  // The estimate's answers are the starting point, so the order is for what
  // was sold: siding's vents and blocks, and a deck, measured on nothing else.
  const stored = estimateRow.data?.generation_answers as {
    siding?: SidingAnswers;
    deck?: DeckAnswers;
  } | null;
  const sidingAnswers =
    sidingOverride ?? stored?.siding ?? defaultSidingAnswers(wallRows);
  const onDeck = isDeckType(picked?.roof_type);
  const storedDeck = stored?.deck;
  const deckAnswers = deckOverride ?? storedDeck ?? defaultDeckAnswers();
  const blocked =
    busy ||
    !templateId ||
    estimateRow.loading ||
    !!estimateRow.error ||
    !estimateRow.data;

  async function generate() {
    if (!onDeck && roofs.length === 0) {
      setError(
        onWalls
          ? t(
              "This job has no wall measurements yet. Add them on the Wall Measurements card first.",
            )
          : t(
              "This job has no roof measurements yet. Take them on the Roof Measurement step first.",
            ),
      );
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const [itemRows, productRows, priceRows, supplierRows] =
        await Promise.all([
          supabase
            .from("roof_template_items")
            .select(
              "id,slot,product_id,qty,default_qty,supplier_id,coverage,section," +
                "applies_when,girth_source,count_source,sort_order",
            )
            .eq("template_id", templateId)
            .order("sort_order"),
          supabase
            .from("products")
            .select(
              // finish and galvalume_twin_id are read by `linesToPlan` for the
              // painted-to-galvalume swap on a metal panel roof.
              "id,name,description,category,kind,unit,default_cost,coverage,waste,measurement,color_rule,color_options,finish,galvalume_twin_id",
            )
            .eq("active", true),
          supabase
            .from("product_prices")
            .select("price_list_id,product_id,cost"),
          supabase.from("suppliers").select("id,price_list_id"),
        ]);

      const templateItems = unwrap<RoofTemplateItem[]>(itemRows);
      if (templateItems.length === 0) {
        setError(
          t(
            '"{name}" has no products on it yet — add them under Tools → Templates.',
            { name: picked?.name ?? "" },
          ),
        );
        setBusy(false);
        return;
      }

      const catalogue = unwrap<
        GeneratorProduct[] & ColorProduct[] & PlanProduct[]
      >(productRows);

      // The same rule the estimate builder uses: which lines this build calls
      // for, in the finish that was chosen. See `linesToPlan`.
      const lines = linesToPlan(picked, templateItems, catalogue, roofs, {
        flat: flatAnswers,
        metal: metalAnswers,
        siding: sidingAnswers,
        deck: deckAnswers,
      });
      if (lines.items.length === 0) {
        setError(
          t('Nothing on "{name}" applies to the build you described.', {
            name: picked?.name ?? "",
          }),
        );
        setBusy(false);
        return;
      }

      const prices = new PriceBook(
        unwrap<ProductPrice[]>(priceRows),
        unwrap<PricedSupplier[]>(supplierRows),
      );
      const plan = generatePlan(
        lines.items,
        lines.roofs,
        catalogue,
        prices,
        supplierId || null,
        // A shingle material order is checked against the roof it is going on,
        // so it reads in the order the roof is built. Metal keeps its own
        // sequence, which is set per template line.
        picked?.roof_type === "Shingle" ? { installOrder: "shingle" } : {},
      );
      // Framing nails in the kind the crew's gun takes — the crew already on
      // this estimate's work order, or the one the template would put there
      // (ADR 0287).
      if (kind === "material") {
        const nails = await fetchFramingNails({
          estimateId,
          trade: picked?.trade ?? trade,
          fallbackCrewId: picked?.default_crew_id ?? null,
        });
        plan.materialItems = fitFramingNails(
          plan.materialItems,
          nails.nailer,
          nails.tag<GeneratorProduct>(catalogue),
          (product, line) =>
            prices.costOf(product, line.supplierId ?? (supplierId || null)),
        );
      }

      // `plan.copyItems` is the package's own wording — What You Get, the
      // warranty, the wood bonus. It is read and then dropped on the floor,
      // which is the whole point of this screen.
      const firstId =
        kind === "material"
          ? await writeMaterialOrders(
              plan.materialItems,
              catalogue,
              lines.questions === "siding"
                ? sidingPanelProductIds(lines.items)
                : null,
            )
          : await writeWorkOrder(plan.workItems);

      if (!firstId) {
        setError(
          kind === "material"
            ? t('"{name}" produced no material lines for this roof.', {
                name: picked?.name ?? "",
              })
            : t('"{name}" produced no labour lines for this roof.', {
                name: picked?.name ?? "",
              }),
        );
        setBusy(false);
        return;
      }

      // Said before the caller closes this screen, which it does the moment
      // the order exists. A painted part on a galvalume order is a material
      // order's problem; the labour is the same either way, so a work order
      // does not repeat it.
      const warnings = kind === "material" ? lines.warnings : [];
      const report = [
        ...(warnings.length > 0
          ? [
              t("Check before the order is sent:") +
                "\n\n" +
                warnings.map((w) => `• ${w}`).join("\n"),
            ]
          : []),
        ...(plan.skipped.length > 0
          ? [
              t("{n} line(s) could not be worked out:", {
                n: plan.skipped.length,
              }) +
                "\n\n" +
                plan.skipped.map((s) => `• ${s.name} — ${s.reason}`).join("\n"),
            ]
          : []),
      ];
      if (report.length > 0) {
        window.alert(t("Created. {report}", { report: report.join("\n\n") }));
      }

      await onCreated(firstId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  /**
   * One order per supplier, as the estimate generator does it.
   *
   * Most templates buy everything from one place; an AG panel roof buys its
   * furring strips and nails from Home Depot while the panels come from Central
   * States, and one order addressed to both is an order neither will fill.
   */
  async function writeMaterialOrders(
    lines: ReturnType<typeof generatePlan>["materialItems"],
    catalogue: readonly ColorProduct[],
    /** The siding itself, when this is a siding order; its lines take the chosen colours. */
    sidingPanels: readonly string[] | null = null,
  ): Promise<string | null> {
    if (lines.length === 0) return null;

    const bySupplier = new Map<string, typeof lines>();
    for (const line of lines) {
      const key = line.supplierId ?? supplierId ?? "";
      bySupplier.set(key, [...(bySupplier.get(key) ?? []), line]);
    }

    let first: string | null = null;
    for (const [supplier, group] of bySupplier) {
      const order = await insertRow<{ id: string }>("material_orders", {
        // Which recipe this order came from, so a check against the estimate
        // can say when it is not the estimate's own (ADR 0156).
        ...generationRecord(templateId, {
          flat: flatAnswers,
          metal: metalAnswers,
          siding: sidingAnswers,
          deck: deckAnswers,
        }),
        job_id: job.id,
        estimate_id: estimateId,
        supplier_id: supplier || null,
        trade: picked?.trade ?? trade,
        status: "not_placed",
        tax_rate: DEFAULT_MATERIAL_TAX_RATE,
        created_by: profileId,
      });
      first ??= order.id;
      for (const line of group) {
        await insertRow("material_order_items", {
          material_order_id: order.id,
          product_id: line.product_id,
          name: line.description,
          qty: line.qty,
          unit: line.unit,
          unit_price: toDecimal(line.unitPrice),
          calc_note: line.calc,
          // Colour-matched products are filled in rather than asked about;
          // everything else stays blank and blocks the order until settled.
          // A siding order takes the panel and trim colours the rep chose.
          color:
            (sidingPanels
              ? sidingLineColor(
                  catalogue.find((p) => p.id === line.product_id),
                  sidingPanels,
                  sidingAnswers,
                )
              : null) ??
            defaultColorFor(catalogue.find((p) => p.id === line.product_id)),
          sort_order: line.sort_order,
        });
      }
    }
    return first;
  }

  async function writeWorkOrder(
    lines: ReturnType<typeof generatePlan>["workItems"],
  ): Promise<string | null> {
    if (lines.length === 0) return null;

    // A template that names nobody leaves the crew blank rather than falling
    // back to whoever is first in the list: a crew we carry workers comp for is
    // charged at a percentage of the work order, so a wrong crew is a wrong
    // cost. A blank one is visible and costs nothing.
    const order = await insertRow<{ id: string }>("work_orders", {
      job_id: job.id,
      estimate_id: estimateId,
      crew_id: picked?.default_crew_id ?? null,
      trades: picked?.trade ? [picked.trade] : trade ? [trade] : null,
      status: "not_scheduled",
      tax_rate: DEFAULT_WORK_TAX_RATE,
      created_by: profileId,
    });
    for (const line of lines) {
      await insertRow("work_order_items", {
        work_order_id: order.id,
        product_id: line.product_id,
        description: line.description,
        qty: line.qty,
        unit: line.unit,
        unit_price: toDecimal(line.unitPrice),
        calc_note: line.calc,
        sort_order: line.sort_order,
      });
    }
    return order.id;
  }

  const noun = kind === "material" ? "Material Order" : "Work Order";

  return (
    <Modal
      title={t("{noun} From Measurements", { noun: t(noun) })}
      subtitle={t(
        "Quantities off this roof, from a template. None of the template's wording comes with them.",
      )}
      onClose={onClose}
      roomy
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            disabled={blocked}
            style={
              blocked ? { opacity: 0.5, cursor: "not-allowed" } : undefined
            }
            onClick={() => void generate()}
          >
            {busy ? t("Generating…") : t("Create {noun}", { noun: t(noun) })}
          </button>
        </>
      }
    >
      {error && <div className="err">{error}</div>}

      <div className="fgrid one">
        <div className="field">
          <label>
            {t("Template")} <span className="req">*</span>
          </label>
          <div
            className="seg"
            role="group"
            aria-label={t("Shingle, metal or flat")}
          >
            {ROOF_PATHS.map((option) => (
              <button
                key={option}
                type="button"
                className={`${PATH_CLASS[option]}${roofPath === option ? " on" : ""}`}
                aria-pressed={roofPath === option}
                onClick={() => {
                  setRoofPath(option);
                  setTemplateId("");
                }}
              >
                {t(option)}
              </button>
            ))}
          </div>
          <div className={`pathlist ${PATH_CLASS[roofPath]}`}>
            <span className="pathname">
              {t("{path} packages", { path: t(roofPath) })}
            </span>
            <select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                const chosen = (templates.data ?? []).find(
                  (tpl) => tpl.id === e.target.value,
                );
                if (chosen?.default_supplier_id) {
                  setSupplierId(chosen.default_supplier_id);
                }
              }}
            >
              <option value="">{t("Package")}</option>
              {offered.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.package_name?.trim()
                    ? `${tpl.package_name} — ${tpl.name}`
                    : tpl.name}
                </option>
              ))}
            </select>
          </div>
          {picked && templateIsUnbuilt(picked) && (
            <div
              className="config-warn"
              role="status"
              style={{ margin: "8px 0 0" }}
            >
              <strong>{t(UNBUILT_TEMPLATE_LABEL)}.</strong>{" "}
              {t(UNBUILT_TEMPLATE_EXPLANATION)}
            </div>
          )}
          {/* The estimate builder's questions, asked here for the same
              template: the answers decide which lines are ordered. */}
          {templateQuestions(picked?.roof_type) === "flat" && (
            <FlatRoofQuestions
              answers={flatAnswers}
              onChange={setFlatAnswers}
            />
          )}
          {templateQuestions(picked?.roof_type) === "metal" && (
            <MetalPanelQuestions
              answers={metalAnswers}
              onChange={setMetalAnswers}
            />
          )}
          {templateQuestions(picked?.roof_type) === "siding" && (
            <SidingQuestions
              answers={sidingAnswers}
              onChange={setSidingAnswers}
              walls={wallRows}
            />
          )}
          {onDeck && (
            <DeckQuestions answers={deckAnswers} onChange={setDeckAnswers} />
          )}
        </div>

        {kind === "material" && (
          <div className="field">
            <label>{t("Supplier")}</label>
            <select
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">{t("Supplier")}</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <span className="na-hint">
              {t(
                "A template line that names its own supplier keeps it, and gets an order of its own.",
              )}
            </span>
          </div>
        )}
      </div>

      {estimateRow.error && (
        <div className="err">
          {t(
            "This estimate could not be read, so its areas in another material cannot be taken off the roof: {error}",
            { error: estimateRow.error },
          )}
        </div>
      )}
      {!estimateRow.loading && !estimateRow.error && !estimateRow.data && (
        <div className="err">
          {t(
            "This estimate could not be found, so an order cannot be sized from it.",
          )}
        </div>
      )}
      {sized.source !== "measured" && (
        <div className="na-hint" role="status" style={{ marginTop: 10 }}>
          {sized.source === "area"
            ? t(
                'This is the estimate\'s "{area}" package, so it is sized to that area alone, as measured when the estimate was generated.',
                { area: sized.areaLabel ?? "" },
              )
            : t(
                "This estimate has areas in another material, so this is sized to the rest of the roof, less those areas, as measured when the estimate was generated.",
              )}
        </div>
      )}
      {!onDeck && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {sized.roofs.length > 0
            ? onWalls
              ? t(
                  "Measuring from {names} — {squares} squares of siding and trim, {waste}% waste.",
                  {
                    names: sized.roofs
                      .map((r) => `"${r.name ?? t("the walls")}"`)
                      .join(" + "),
                    squares: sized.roofs
                      .reduce((total, r) => total + sidingSquaresOf(r), 0)
                      .toFixed(2),
                    waste: Math.max(
                      0,
                      ...sized.roofs.map((r) => Number(r.waste_pct ?? 0)),
                    ),
                  },
                )
              : t(
                  "Measuring from {names} — {squares} squares in total, {waste}% waste.",
                  {
                    names: sized.roofs
                      .map((r) => `"${r.name ?? t("the roof layout")}"`)
                      .join(" + "),
                    squares: sized.roofs.reduce(
                      (total, r) => total + Number(r.squares ?? 0),
                      0,
                    ),
                    waste: Math.max(
                      0,
                      ...sized.roofs.map((r) => Number(r.waste_pct ?? 0)),
                    ),
                  },
                )
            : onWalls
              ? t(
                  "No wall measurements on this job yet. Add them on the Wall Measurements card first.",
                )
              : t(
                  "No roof measurements on this job yet. Take them on the Roof Measurement step first.",
                )}
        </div>
      )}

      <p className="n">
        {t(
          "This writes {kind} lines and nothing else. The estimate's line items, its scope of work and its price are left exactly as they are.",
          { kind: kind === "material" ? t("material") : t("labour") },
        )}
      </p>
    </Modal>
  );
}
